// COD-1091: load Sprig (Actions) for a stable 40% sample. On test, load it for every user.
// ?sprig=on / ?sprig=off apply only to the document load that contains them.

export const SPRIG_INTEGRATION_NAME = 'Sprig (Actions)';
export const SPRIG_SAMPLE_RATE = 0.4;

const SAMPLE_KEY = 'hw_sprig_sample_key';
const USER_ID_KEY = 'ajs_user_id';
const ANONYMOUS_ID_KEY = 'ajs_anonymous_id';
// Set when resolve runs against the document URL. Later events reuse it.
let documentInSample = null;
const FNV_OFFSET_BASIS = 0x811C9DC5;
const FNV_PRIME = 0x01000193;
const UINT32_RANGE = 4294967296;

function parseStoredId(value) {
  if (value == null) {
    return '';
  }

  const raw = String(value).trim();
  if (!raw) {
    return '';
  }

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string' || typeof parsed === 'number') {
      return String(parsed);
    }
  } catch {
    // Value is not JSON. Fall through and strip wrapping quotes.
  }

  if (raw.length >= 2 && raw.startsWith('"') && raw.endsWith('"')) {
    return raw.slice(1, -1);
  }

  return raw;
}

const locationSearch = () => (typeof window === 'undefined' ? '' : window.location.search);

function sprigUrlOverride(search = locationSearch()) {
  const value = new URLSearchParams(search).get('sprig');

  if (value === 'on') {
    return true;
  }

  if (value === 'off') {
    return false;
  }

  return null;
}

function inSampleFor(sampleKey, override, releaseEnv) {
  if (override !== null) {
    return override;
  }

  if (releaseEnv === 'test') {
    return true;
  }

  if (!sampleKey) {
    return false;
  }

  return hashToBucket(sampleKey) < SPRIG_SAMPLE_RATE;
}

export function hashToBucket(id) {
  const value = String(id ?? '');
  let hash = FNV_OFFSET_BASIS;

  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, FNV_PRIME);
  }

  return (hash >>> 0) / UINT32_RANGE;
}

const readId = (storage, key) => parseStoredId(storage.getItem(key));

function sampleResult(sampleKey, override, releaseEnv, search) {
  const inSample = inSampleFor(sampleKey, override, releaseEnv);

  if (search === undefined) {
    documentInSample = inSample;
  }

  return {
    sampleKey: sampleKey || '',
    inSample,
  };
}

export function clearSprigSampleKey(storage) {
  const store = storage === undefined
    ? (typeof window === 'undefined' ? null : window.localStorage)
    : storage;

  if (!store) {
    return;
  }

  try {
    store.removeItem(SAMPLE_KEY);
  } catch {
    // Storage can throw when it is blocked.
  }
}

export function resolveSprigSample({
  storage,
  userId,
  createId,
  search,
  releaseEnv,
} = {}) {
  const override = sprigUrlOverride(search);

  try {
    if (!storage) {
      return sampleResult('', override, releaseEnv, search);
    }

    let sampleKey = readId(storage, SAMPLE_KEY);

    if (!sampleKey) {
      const explicitUserId = userId ? String(userId) : '';
      sampleKey = explicitUserId || readId(storage, USER_ID_KEY) || readId(storage, ANONYMOUS_ID_KEY);

      if (!sampleKey && typeof createId === 'function') {
        const created = createId();
        sampleKey = created ? String(created) : '';
      }

      if (sampleKey) {
        storage.setItem(SAMPLE_KEY, JSON.stringify(sampleKey));
      }
    }

    return sampleResult(sampleKey, override, releaseEnv, search);
  } catch {
    return sampleResult('', override, releaseEnv, search);
  }
}

export function isSprigInSample(storage, search, releaseEnv) {
  if (search === undefined && documentInSample !== null) {
    return documentInSample;
  }

  const override = sprigUrlOverride(search);

  try {
    const store = storage ?? (typeof window === 'undefined' ? null : window.localStorage);

    if (!store) {
      return inSampleFor('', override, releaseEnv);
    }

    return inSampleFor(readId(store, SAMPLE_KEY), override, releaseEnv);
  } catch {
    return inSampleFor('', override, releaseEnv);
  }
}
